import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { v2MenuItemSizeClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import {
  ArrowLeftRight,
  Clock,
  Handshake,
  LogOut,
  type LucideIcon,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { ColoredAvatar, ShortcutText, Tooltip } from 'src/components';
import { OptionSwitchMenu } from 'src/components/Navbar/AppMenu/option-switch-menu';
import { useCanSwitchPartner } from 'src/components/Navbar/use-can-switch-partner';
import { useThemeSwitch } from 'src/components/Navbar/use-theme-switch';
import { useTimeFormatSwitch } from 'src/components/Navbar/use-time-format-switch';
import { appShortcuts } from 'src/constants/appShortcuts';
import { useSelectedPartner } from 'src/graphql';
import { useTimeFormat } from 'src/graphql/entities/time-format/use-time-format';
import { Modals, useModalMethods } from 'src/store/modals';
import { useLogout, useRegisterShortcuts } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import { SupportLinks } from './SupportLinks/support-links';

const REFER_PARTNER_LINK = 'https://www.onevisionresources.com/refer-a-partner';

const THEME_ICONS: Record<string, LucideIcon> = {
  light_mode: Sun,
  dark_mode: Moon,
  contrast: Monitor,
};

export const AppMenu = ({
  anchor,
  onClose,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
}) => {
  const {
    selectedTheme,
    setForceThemeMode,
    themeModalAnchor,
    openThemeSwitch,
    closeThemeSwitch,
    themeOptions,
  } = useThemeSwitch();

  const { isDesktop } = useScreenDimensions();
  const { logout } = useLogout();

  const { selectedPartner } = useSelectedPartner();
  const { showModal: showSelectPartnerModal } = useModalMethods(Modals.SelectPartnerModal);

  const [clockFormatSwitchAnchor, setClockFormatSwitchAnchor] = useState<null | HTMLElement>(
    null,
  );

  const { currentTimeFormat, changeTimeFormat } = useTimeFormat();

  const canOpenPartnerDialog = useCanSwitchPartner();

  useRegisterShortcuts(
    useMemo(
      () =>
        canOpenPartnerDialog
          ? [
              {
                ...appShortcuts.openSelectPartnerDialog,
                callback: () => {
                  showSelectPartnerModal(null);
                  onClose();
                  return false;
                },
              },
            ]
          : [],
      [canOpenPartnerDialog, onClose, showSelectPartnerModal],
    ),
  );
  const { selectedTimeFormatLabel, timeFormatsMenu } = useTimeFormatSwitch({
    isOpen: Boolean(clockFormatSwitchAnchor),
    anchorEl: clockFormatSwitchAnchor,
    selectedFormat: currentTimeFormat,
    onClose: () => setClockFormatSwitchAnchor(null),
    onChange: (value) => {
      changeTimeFormat(value);
      onClose();
    },
  });

  const switchPartnerTooltip = (
    <ShortcutText
      label="Switch Partner"
      shortcut={appShortcuts.openSelectPartnerDialog.command}
      size="small"
    />
  );

  const switchPartnerMenuItem = selectedPartner ? (
    <>
      <ListItemIcon>
        <ColoredAvatar name={selectedPartner.businessName} size="small" />
      </ListItemIcon>
      <Tooltip title={switchPartnerTooltip}>
        <ListItemText slotProps={{ primary: { noWrap: true } }}>
          {selectedPartner.businessName}
        </ListItemText>
      </Tooltip>
      <ArrowLeftRight />
    </>
  ) : (
    <>
      <ListItemIcon>
        <ArrowLeftRight />
      </ListItemIcon>
      <Tooltip title={switchPartnerTooltip}>
        <ListItemText>Switch Partner</ListItemText>
      </Tooltip>
    </>
  );

  const openClockFormatSwitch = (event: MouseEvent<HTMLElement>) => {
    setClockFormatSwitchAnchor((current) => (current ? null : event.currentTarget));
  };

  const openReferPartnerLink = useCallback(() => {
    window.open(REFER_PARTNER_LINK, '_blank');
    onClose();
  }, [onClose]);

  const ThemeIcon = THEME_ICONS[selectedTheme?.icon ?? ''] ?? Monitor;

  return (
    <V2ThemeProvider>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        open={Boolean(anchor)}
        onClose={onClose}
      >
        <MenuItem
          className={v2MenuItemSizeClass.large}
          data-testid="app-menu-switch-partner"
          disabled={!canOpenPartnerDialog}
          onClick={() => {
            if (!canOpenPartnerDialog) return;
            showSelectPartnerModal(null);
            onClose();
          }}
        >
          {switchPartnerMenuItem}
        </MenuItem>

        {selectedPartner?.currentPartner === 'yes' && (
          <SupportLinks anchor={anchor} onCloseMenu={onClose} />
        )}

        <MenuItem className={v2MenuItemSizeClass.large} onClick={openThemeSwitch}>
          <ListItemIcon>
            <ThemeIcon />
          </ListItemIcon>
          <ListItemText>{selectedTheme?.label} Theme</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem className={v2MenuItemSizeClass.large} onClick={openClockFormatSwitch}>
          <ListItemIcon>
            <Clock />
          </ListItemIcon>
          <ListItemText>{selectedTimeFormatLabel}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem className={v2MenuItemSizeClass.large} onClick={openReferPartnerLink}>
          <ListItemIcon>
            <Handshake />
          </ListItemIcon>
          <ListItemText>Refer a Partner ($)</ListItemText>
        </MenuItem>

        <MenuItem className={v2MenuItemSizeClass.large} onClick={logout}>
          <ListItemIcon>
            <LogOut />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>

      {timeFormatsMenu}

      <OptionSwitchMenu
        anchorEl={themeModalAnchor}
        onClose={closeThemeSwitch}
        selectedValue={selectedTheme?.value}
        options={themeOptions.map((option) => ({
          value: option.value,
          label: option.label,
          icon: THEME_ICONS[option.icon] ?? Monitor,
        }))}
        onSelect={(value) => {
          setForceThemeMode(value);
          closeThemeSwitch();
          onClose();
        }}
      />
    </V2ThemeProvider>
  );
};
